import React, { useRef, useEffect } from 'react';
import { FormTheme } from '../types/formTheme';

interface FormTextareaProps {
    id: string;
    value: string;
    onChange: (value: string) => void;
    onBlur?: () => void;
    placeholder?: string;
    required?: boolean;
    disabled?: boolean;
    error?: string;
    maxLength?: number;
    rows?: number;
    autoResize?: boolean;
    appearance?: FormTheme;
    className?: string;
}

const FormTextarea: React.FC<FormTextareaProps> = ({
    id,
    value,
    onChange,
    onBlur,
    placeholder,
    required = false,
    disabled = false,
    error,
    maxLength,
    rows = 4,
    autoResize = true,
    appearance = 'default',
    className = ''
}) => {
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        onChange(e.target.value);
    };

    // Auto-resize textarea based on content
    useEffect(() => {
        if (autoResize && textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
        }
    }, [value, autoResize]);

    return (
        <div className="relative">
            <textarea
                ref={textareaRef}
                id={id}
                value={value}
                onChange={handleChange}
                onBlur={onBlur}
                placeholder={placeholder}
                required={required}
                disabled={disabled}
                maxLength={maxLength}
                rows={rows}
                className={`
                    ${appearance === 'tracking'
                        ? `
                            w-full px-4 py-3 rounded-lg
                            bg-white border transition-all duration-200
                            text-gray-900 placeholder-gray-400
                            focus:outline-none focus:ring-2 focus:ring-offset-0
                            disabled:opacity-50 disabled:cursor-not-allowed
                            resize-none
                        `
                        : `
                            w-full px-4 py-3 rounded-xl
                            bg-white/80 backdrop-blur-sm
                            border-2 transition-all duration-200
                            text-gray-900 placeholder-gray-400
                            focus:outline-none focus:ring-2 focus:ring-offset-2
                            disabled:opacity-50 disabled:cursor-not-allowed
                            resize-none
                        `
                    }
                    ${error
                        ? 'border-red-400 focus:border-red-500 focus:ring-red-500/20'
                        : appearance === 'tracking'
                            ? 'border-gray-300 focus:border-black focus:ring-black/10 hover:border-gray-400'
                            : 'border-gray-200 focus:border-blue-500 focus:ring-blue-500/20 hover:border-gray-300'
                    }
                    ${className}
                `}
                aria-invalid={error ? 'true' : 'false'}
                aria-describedby={error ? `${id}-error` : undefined}
            />
            {maxLength && (
                <div className="mt-2 text-xs text-gray-500 text-right">
                    {value.length} / {maxLength}
                </div>
            )}
        </div>
    );
};

export default FormTextarea;
